import type { ShapeTemplateType, UserSettingConfig } from '../data/type'
import { Defect } from '../data/types'
import { sortByKeys } from '../util'
import { getCurrentColor } from '../constant'
import { getUnixtime } from '@/lib'
import { cloneDeep } from 'lodash'
import { toRaw } from 'vue'

// shape_data가 문자열로 저장된 상태를 객체로 정규화 (파싱 실패 시 null)
function normalizeShapeDataObject(raw: unknown): Defect['shape_data'] | null {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  return raw as Defect['shape_data']
}

/**
 * 패널 스냅샷 저장 직전, 캔버스(스토어)가 소유하는 필드를 최신 스토어 row 기준으로 rebase합니다.
 * 패널이 열린 뒤의 번호풍선 이동·도형 변형이 stale 스냅샷 전송으로 되돌아가는 유실을 방지합니다.
 * @param target 전송 직전의 패널 스냅샷 (제자리 수정)
 * @param latest 스토어에서 방금 조회한 최신 row (없으면 아무것도 하지 않음)
 */
export function rebaseCanvasOwnedFields(target: Defect, latest: Defect | null | undefined): void {
  if (!latest) return
  target.is_current = latest.is_current
  target.color = latest.color
  const latestShapeData = normalizeShapeDataObject(latest.shape_data)
  if (target.shape_data && latestShapeData) {
    target.shape_data = cloneDeep(latestShapeData)
  }
}

/**
 * 캔버스 importEntity에 넘길 shape_data. 반응형 프록시를 벗긴 얕은 사본(삼각대 점만 예외 — 아래 주석).
 * points 배열이 프록시째 Konva Line에 들어가면 점 읽기(그리기·기하 hit 구축·스냅샷 시그니처·export)가
 * 프록시 트랩을 거쳐 원본보다 약 100배 느리다(A21s 31,246점 140ms → 1.4ms).
 * 엔진은 points를 제자리에서 고치지 않고 늘 새 배열로 바꾸므로(지우개 분할·획 추가·이동·export 복사) 반응성과 무관하다
 */
export function canvasShapeData<T>(shapeData: T): T {
  if (!shapeData || typeof shapeData !== 'object') return shapeData
  // 반응형 shape_data를 펼치면 키마다 프록시 트랩 — 층 진입 483개에 ≈95ms(A21s). 원본을 펼친다.
  // 엔진은 가져온 중첩 값을 읽기만 하거나 새 배열로 바꾸는데, 삼각대 점(tripodShapePoints)만은 참조째 들고
  // 제자리에서 고친다(tripodSelectionMixin) → 그 키는 예전처럼 반응형 그대로 넘겨 스토어 반응성을 유지한다
  const raw = toRaw(shapeData) as Record<string, unknown>
  const out: Record<string, unknown> = { ...raw }
  if (out.brushLines) out.brushLines = toRaw(out.brushLines)
  if (raw.tripodShapePoints) {
    out.tripodShapePoints = (shapeData as Record<string, unknown>).tripodShapePoints
  }
  return out as T
}

// 객체를 쿼리스트링으로 변환하는 함수
export function toQueryString<T extends Record<string, unknown>>(data: Partial<T>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

// 두 객체를 비교(얕게)하여 값이 다른 키를 배열로 반환하는 함수
export function findDifferences<T extends Record<string, unknown>>(a: T, b: T): string[] {
  const differences: string[] = []
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)])
  allKeys.forEach(key => {
    if (a[key] !== b[key]) differences.push(key)
  })
  return differences
}

// 두 객체를 비교해서 변화된 데이터를 추출
export function getDifferencesData<T extends object>(oldItem: T, newItem: T): Partial<T> {
  return Object.entries(newItem).reduce((acc, [key, value]) => {
    if (oldItem[key as keyof T] !== value) {
      acc[key as keyof T] = value
    }
    return acc
  }, {} as Partial<T>)
}

// 폭 값을 숫자로 변환하는 함수
export function parseWidthValue(
  width: string | number, // 단위 mm (입력값은 문자열 또는 숫자)
  unit: 'mm' | 'm' = 'mm',
): number | null {
  if (width === null || width === undefined) return null

  const str = width.toString().trim()
  if (str === '') return null

  let value: number | null = null

  // 숫자만 있는 경우
  if (/^\d+(\.\d+)?$/.test(str)) value = parseFloat(str)
  // 범위 패턴 처리
  else if (/^([0-9.]*)(~)([0-9.]*)$/.test(str)) {
    const parts = str.split('~')
    const start = parts[0] ? parseFloat(parts[0]) : null
    const end = parts[1] ? parseFloat(parts[1]) : null

    if (start !== null && end !== null) {
      value = (start + end) / 2
    } else if (end !== null) {
      value = end
    } else if (start !== null) {
      value = start
    }
  }
  // 텍스트가 포함된 패턴 처리 ('~5mm미만', '1mm이상~3mm미만', '1mm이상')
  else {
    const endWithTextMatch = str.match(/^~([0-9.]+)/)
    const rangeWithTextMatch = str.match(/([0-9.]+)[^0-9.]+([0-9.]+)/)
    const startWithTextMatch = str.match(/([0-9.]+)/)

    if (rangeWithTextMatch) {
      const start = parseFloat(rangeWithTextMatch[1])
      const end = parseFloat(rangeWithTextMatch[2])
      value = parseFloat(((start + end) / 2).toFixed(6))
    } else if (endWithTextMatch) {
      value = parseFloat(endWithTextMatch[1])
    } else if (startWithTextMatch) {
      value = parseFloat(startWithTextMatch[1])
    }
  }

  if (value === null) return null

  // 단위 변환
  if (unit === 'm') return value / 1000

  return value
}

// 길이 값을 숫자로 변환하는 함수
export function parseLengthValue(
  length: string | number, // 단위 mm
  unit: 'mm' | 'm' = 'mm',
): number | null {
  if (!length || length.toString().trim() === '') return null

  const num = parseFloat(length.toString())
  if (isNaN(num)) return null

  // 단위 변환
  const converted = unit === 'm' ? num / 1000 : num

  return parseFloat(converted.toFixed(4))
}

// 면적을 계산하는 함수 (제곱미터 반환)
export function calculateArea(
  width: string | number,
  length: string | number,
  widthUnit: 'mm' | 'm' = 'mm',
  lengthUnit: 'mm' | 'm' = 'mm',
): number | null {
  const parsedWidth = parseWidthValue(width.toString(), widthUnit)
  const parsedLength = parseLengthValue(length, lengthUnit)

  if (!parsedWidth || parsedWidth <= 0 || !parsedLength || parsedLength <= 0) return null

  // 단위 변환
  const widthInM = widthUnit === 'mm' ? parsedWidth / 1000 : parsedWidth
  const lengthInM = lengthUnit === 'mm' ? parsedLength / 1000 : parsedLength

  const area = widthInM * lengthInM

  // 소수점 넷째자리 반올림
  return Math.round(area * 10000) / 10000
}

// 개인설정에서 키들로 값을 찾는 함수 (없으면 무조건 undefined)
export function findUserSettingValue(
  configData: UserSettingConfig[],
  groupKey: string,
  settingKey: string,
  optionKey?: string,
): string | number | boolean | undefined {
  const group = configData.find(g => g.key === groupKey)
  if (!group) return undefined

  const setting = group.settings.find(s => s.key === settingKey)
  if (!setting) return undefined

  // value가 배열인 경우 (optionKey가 있어야 함)
  if (Array.isArray(setting.value)) {
    if (optionKey == null) return undefined
    const index = setting.values.findIndex(v => v.key === optionKey)
    if (index === -1) return undefined
    return setting.value[index]
  }

  // 단일 값인 경우
  return setting.value
}

// 개인설정을 기반으로 결함의 기본값을 가져오는 함수
export function getDefectDefaultValueFromUserSetting(configData: UserSettingConfig[]): {
  template: ShapeTemplateType // 구조 (접두사 'custom-')
  structural_component: string // 부재
  classification: string // 결함
  is_structural: number // 구조체 여부
  width: string // 폭
  length: string // 길이
  count: string // 개수
} {
  const defaultData = {
    template: 'custom-RC' as ShapeTemplateType,
    structural_component: '',
    classification: '',
    is_structural: 0,
    width: '',
    length: '',
    count: '',
  }

  // 구조
  const templateValue = findUserSettingValue(
    configData,
    'defect',
    'defaultDefectValue',
    'STRUCTURE',
  )
  if (templateValue !== undefined && typeof templateValue === 'string') {
    defaultData.template = `custom-${templateValue}` as ShapeTemplateType
  }

  // 부재
  const structuralComponentValue = findUserSettingValue(
    configData,
    'defect',
    'defaultDefectValue',
    'MEMBER',
  )
  if (structuralComponentValue !== undefined && typeof structuralComponentValue === 'string') {
    defaultData.structural_component = structuralComponentValue
  }

  // 결함
  const classificationValue = findUserSettingValue(
    configData,
    'defect',
    'defaultDefectValue',
    'DEFECT',
  )
  if (classificationValue !== undefined && typeof classificationValue === 'string') {
    defaultData.classification = classificationValue
  }

  // 구조체 여부
  const isStructuralShow = findUserSettingValue(
    configData,
    'defect',
    'hideDefectSection',
    'IS_STRUCTURAL',
  )
  if (isStructuralShow) {
    const isStructuralValue = findUserSettingValue(
      configData,
      'defect',
      'defaultDefectValue',
      'IS_STRUCTURAL',
    )
    if (
      isStructuralValue !== undefined &&
      (isStructuralValue === '비구조체' || isStructuralValue === '구조체')
    ) {
      defaultData.is_structural = isStructuralValue === '구조체' ? 1 : 0
    }
  }
  // 폭, 길이, 면적
  const widthLengthAreaShow = findUserSettingValue(
    configData,
    'defect',
    'hideDefectSection',
    'WIDTH_LENGTH_AREA',
  )

  if (widthLengthAreaShow) {
    // 폭
    const widthValue = findUserSettingValue(configData, 'defect', 'defaultDefectValue', 'WIDTH')
    if (widthValue !== undefined && typeof widthValue === 'string' && !isNaN(Number(widthValue))) {
      defaultData.width = widthValue
    }
    // 길이
    const lengthValue = findUserSettingValue(configData, 'defect', 'defaultDefectValue', 'LENGTH')
    if (
      lengthValue !== undefined &&
      typeof lengthValue === 'string' &&
      !isNaN(Number(lengthValue))
    ) {
      defaultData.length = lengthValue
    }
  }

  // 개수
  const countValue = findUserSettingValue(configData, 'defect', 'defaultDefectValue', 'COUNT')
  if (countValue !== undefined && typeof countValue === 'string' && !isNaN(Number(countValue))) {
    defaultData.count = countValue
  }

  return defaultData
}

/**
 * Defect 배열을 sort_key(fractional-indexing)를 기준으로 정렬하는 함수
 * 서버 동기화된 항목과 로컬 항목을 구분하여 정렬
 * @param a - 첫 번째 Defect
 * @param b - 두 번째 Defect
 * @returns 정렬 결과 (-1, 0, 1)
 */
export function sortShapesByIdAndSelectedTags(a: Defect, b: Defect): number {
  const aIsServerSync = !String(a.id).includes('_need_to_upload')
  const bIsServerSync = !String(b.id).includes('_need_to_upload')

  // 하나만 서버 동기화된 경우 - 서버 동기화된 것을 앞에
  if (aIsServerSync && !bIsServerSync) return -1
  if (!aIsServerSync && bIsServerSync) return 1

  // 둘 다 서버 동기화 또는 둘 다 로컬인 경우 - sortByKeys 사용
  return sortByKeys([a, b]).indexOf(a) === 0 ? -1 : 1
}

// 개인설정에서 각 항목별 노출 여부를 가져오는 함수
export function getDefectShowFromUserSetting(configData: UserSettingConfig[]): {
  STRUCTURE: boolean // 구조
  MEMBER: boolean // 부재
  DEFECT: boolean // 결함유형
  IS_STRUCTURAL: boolean // 구조체 여부
  WIDTH_LENGTH_AREA: boolean // 폭, 길이, 면적
  CAUSE: boolean // 발생원인
  REMEDY: boolean // 보수방안
} {
  const defaultData = {
    STRUCTURE: true,
    MEMBER: true,
    DEFECT: true,
    IS_STRUCTURAL: true,
    WIDTH_LENGTH_AREA: true,
    CAUSE: true,
    REMEDY: true,
  }
  // 구조
  const structureValue = findUserSettingValue(
    configData,
    'defect',
    'hideDefectSection',
    'STRUCTURE',
  )
  if (structureValue !== undefined && typeof structureValue === 'boolean') {
    defaultData.STRUCTURE = structureValue
  }
  // 부재
  const memberValue = findUserSettingValue(configData, 'defect', 'hideDefectSection', 'MEMBER')
  if (memberValue !== undefined && typeof memberValue === 'boolean') {
    defaultData.MEMBER = memberValue
  }
  // 결함유형
  const defectValue = findUserSettingValue(configData, 'defect', 'hideDefectSection', 'DEFECT')
  if (defectValue !== undefined && typeof defectValue === 'boolean') {
    defaultData.DEFECT = defectValue
  }
  // 구조체 여부
  const isStructuralValue = findUserSettingValue(
    configData,
    'defect',
    'hideDefectSection',
    'IS_STRUCTURAL',
  )
  if (isStructuralValue !== undefined && typeof isStructuralValue === 'boolean') {
    defaultData.IS_STRUCTURAL = isStructuralValue
  }
  // 폭, 길이, 면적
  const widthLengthAreaValue = findUserSettingValue(
    configData,
    'defect',
    'hideDefectSection',
    'WIDTH_LENGTH_AREA',
  )
  if (widthLengthAreaValue !== undefined && typeof widthLengthAreaValue === 'boolean') {
    defaultData.WIDTH_LENGTH_AREA = widthLengthAreaValue
  }
  // 발생원인
  const causeValue = findUserSettingValue(configData, 'defect', 'hideDefectSection', 'CAUSE')
  if (causeValue !== undefined && typeof causeValue === 'boolean') {
    defaultData.CAUSE = causeValue
  }
  // 보수방안
  const remedyValue = findUserSettingValue(configData, 'defect', 'hideDefectSection', 'REMEDY')
  if (remedyValue !== undefined && typeof remedyValue === 'boolean') {
    defaultData.REMEDY = remedyValue
  }
  return defaultData
}

// 값이 목록에 없는 수동 입력값인지 확인
export function isManualValue(
  value: string | undefined | null,
  list: readonly { name: string }[],
): boolean {
  if (!value) return false
  return !list.some(item => item.name === value)
}

// shapeId와 관련된 모든 Shape들을 반환하는 함수 (계층 트리 전체)
export function getRelatedShapes(shapeId: number | string, shapes: Defect[]): Defect[] {
  const allShapes = shapes

  // shapeId에 해당하는 Defect 찾기 (localStringId와 id 모두 확인)
  const targetShape = allShapes.find(shape => {
    return (
      String(shape.id) == String(shapeId) ||
      ('localStringId' in shape &&
        shape.localStringId &&
        String(shape.localStringId) == String(shapeId))
    )
  })
  if (!targetShape) return []

  // 루트 Defect 찾기 (parent_shape_id가 -1인 최상위 부모)
  let rootShape = targetShape
  while (rootShape.parent_defect_id !== -1) {
    const parent = allShapes.find(shape => {
      // parent_shape_id가 localStringId인 경우와 서버 ID인 경우 모두 처리
      return (
        String(shape.id) == String(rootShape.parent_defect_id) ||
        ('localStringId' in shape &&
          shape.localStringId &&
          String(shape.localStringId) == String(rootShape.parent_defect_id))
      )
    })
    if (!parent) break
    rootShape = parent
  }

  // 루트부터 시작해서 모든 하위 Shape들을 수집
  function collectAllShapes(shape: Defect, visited: Set<string>): Defect[] {
    const shapeIdStr =
      'localStringId' in shape && shape.localStringId
        ? String(shape.localStringId)
        : String(shape.id)
    if (visited.has(shapeIdStr)) return []

    visited.add(shapeIdStr)
    const result = [shape]

    // parent_shape_id로 자식 Shape들 찾기 및 재귀 호출
    const children = allShapes.filter(s => {
      // parent_shape_id가 현재 shape의 id 또는 localStringId와 일치하는지 확인
      return (
        String(s.parent_defect_id) === String(shape.id) ||
        ('localStringId' in shape &&
          shape.localStringId &&
          String(s.parent_defect_id) === String(shape.localStringId))
      )
    })
    for (const child of children) {
      result.push(...collectAllShapes(child, visited))
    }

    return result
  }

  const visited = new Set<string>()
  const relatedShapes = collectAllShapes(rootShape, visited)

  // 정렬 - parent_defect_id = -1 + ID 기준 (shape.ts와 동일한 로직)
  return relatedShapes.sort((a, b) => {
    // parent_shape_id가 -1인 것이 우선
    if (a.parent_defect_id === -1 && b.parent_defect_id !== -1) return -1
    if (a.parent_defect_id !== -1 && b.parent_defect_id === -1) return 1

    // 동일한 계층 내에서 ID 기준 안정적 정렬
    return sortShapesByIdAndSelectedTags(a, b)
  })
}

type BalloonColors = { borderColor: string; fillColor: string; fontColor: string }

// 설정 배열에서 특정 setting의 value를 직접 읽는 내부 함수
function findSettingArrayValue(
  configData: UserSettingConfig[],
  settingKey: string,
): string[] | null {
  for (const group of configData) {
    const setting = group.settings?.find(s => s.key === settingKey)
    if (setting && Array.isArray(setting.value)) return setting.value as string[]
  }
  return null
}

// 보수완료 자동 색상 변경이 활성화된 경우 repairCompleteBalloonStyle 색상을 반환
export function getRepairBalloonColors(configData: UserSettingConfig[]): BalloonColors | null {
  const autoChange = findUserSettingValue(configData, 'style', 'repairCompleteBalloonAutoChange')
  if (autoChange !== 1) return null

  const styleArr = findSettingArrayValue(configData, 'repairCompleteBalloonStyle')
  if (!styleArr || styleArr.length < 3) return null

  return {
    borderColor: getCurrentColor(styleArr[0]) || getCurrentColor('red'),
    fillColor: getCurrentColor(styleArr[1]),
    fontColor: getCurrentColor(styleArr[2]) || getCurrentColor('red'),
  }
}

// 기본 balloonStyle 색상을 반환
export function getDefaultBalloonColors(configData: UserSettingConfig[]): BalloonColors {
  const styleArr = findSettingArrayValue(configData, 'balloonStyle')
  if (!styleArr || styleArr.length < 3) {
    return {
      borderColor: getCurrentColor('red'),
      fillColor: '',
      fontColor: getCurrentColor('red'),
    }
  }
  return {
    borderColor: getCurrentColor(styleArr[0]) || getCurrentColor('red'),
    fillColor: getCurrentColor(styleArr[1]),
    fontColor: getCurrentColor(styleArr[2]) || getCurrentColor('red'),
  }
}

// shape 객체에 풍선 스타일 색상을 직접 적용
export function applyBalloonStyleToShape(shape: Defect, colors: BalloonColors): void {
  if (!shape.shape_data) return
  shape.color = colors.borderColor
  ;(shape as Record<string, unknown>)['color_modified'] = getUnixtime()
  const sd = typeof shape.shape_data === 'string' ? JSON.parse(shape.shape_data) : shape.shape_data
  sd.color = colors.borderColor
  sd.balloonFillColor = colors.fillColor
  sd.balloonFontColor = colors.fontColor
  shape.shape_data = sd
}
